import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { shuffleOptionsWithCorrect } from '@/lib/shuffle';
import { learningPaths, type Question } from '@/data/lessons';
import { soccerLearningPaths } from '@/data/soccerLessons';
import { basketballLearningPaths } from '@/data/basketballLessons';
import { baseballLearningPaths } from '@/data/baseballLessons';
import { Mascot } from '@/components/Mascot';
import { usePreferredSport } from '@/hooks/usePreferredSport';

interface HeartsDepletedOverlayProps {
  completedLessons: string[];
  onPracticeComplete: () => void; // called when user earns a heart back
  onExit: () => void;
}

function getTimeUntilNextHeart(): string {
  const lastLost = localStorage.getItem('hearts_last_lost_at');
  if (!lastLost) return 'under 1 hour';
  const elapsed = Date.now() - parseInt(lastLost, 10);
  const remaining = Math.max(0, 60 * 60 * 1000 - (elapsed % (60 * 60 * 1000)));
  const mins = Math.ceil(remaining / 60000);
  if (mins >= 60) return '1 hour';
  return `${mins} minute${mins !== 1 ? 's' : ''}`;
}

function gatherPracticeQuestions(completedLessons: string[]): Question[] {
  const allPaths = [...learningPaths, ...soccerLearningPaths, ...basketballLearningPaths, ...baseballLearningPaths];
  const pool: Question[] = [];

  for (const path of allPaths) {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        if (completedLessons.includes(lesson.id)) {
          for (const q of lesson.questions) {
            if (q.type === 'multiple-choice' && q.options.length >= 2) {
              pool.push(q);
            }
          }
        }
      }
    }
  }

  // Shuffle and pick 3
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 3);
}

function BrokenHeartSVG() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M40 70C40 70 8 50 8 28C8 16 17 8 27 8C33 8 38 12 40 16C42 12 47 8 53 8C63 8 72 16 72 28C72 50 40 70 40 70Z"
        fill="hsl(var(--destructive))"
        opacity="0.2"
        stroke="hsl(var(--destructive))"
        strokeWidth="2"
      />
      {/* Crack line */}
      <path
        d="M40 16L36 30L44 36L38 52L40 70"
        stroke="hsl(var(--background))"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Left half darker */}
      <path
        d="M40 16L36 30L44 36L38 52L40 70C40 70 8 50 8 28C8 16 17 8 27 8C33 8 38 12 40 16Z"
        fill="hsl(var(--destructive))"
        opacity="0.35"
      />
    </svg>
  );
}

type PracticePhase = 'prompt' | 'practicing' | 'done';

export function HeartsDepletedOverlay({ completedLessons, onPracticeComplete, onExit }: HeartsDepletedOverlayProps) {
  const { preferredSport } = usePreferredSport();
  const [timeStr, setTimeStr] = useState(getTimeUntilNextHeart);
  const [practicePhase, setPracticePhase] = useState<PracticePhase>('prompt');
  const [practiceQs] = useState(() => gatherPracticeQuestions(completedLessons));
  const [qIndex, setQIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [correctCount, setCorrectCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTimeStr(getTimeUntilNextHeart()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const currentQ = practiceQs[qIndex];
  const shuffled = useMemo(() => {
    if (!currentQ) return { shuffledOptions: [] as string[], shuffledCorrectIndex: 0 };
    return shuffleOptionsWithCorrect(currentQ.options, currentQ.correctIndex);
  }, [currentQ]);

  const handleAnswer = useCallback((index: number) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    const correct = index === shuffled.shuffledCorrectIndex;
    setIsCorrect(correct);
    if (correct) setCorrectCount(c => c + 1);
  }, [selectedAnswer, shuffled.shuffledCorrectIndex]);

  const handleNext = useCallback(() => {
    if (qIndex < practiceQs.length - 1) {
      setQIndex(i => i + 1);
      setSelectedAnswer(null);
      setIsCorrect(null);
    } else {
      // Practice round complete
      if (correctCount + (isCorrect ? 0 : 0) >= 2) {
        // Need at least 2/3 correct
        setPracticePhase('done');
        setTimeout(() => onPracticeComplete(), 1500);
      } else {
        setPracticePhase('done');
        setTimeout(() => onPracticeComplete(), 1500);
      }
    }
  }, [qIndex, practiceQs.length, correctCount, isCorrect, onPracticeComplete]);

  const canPractice = practiceQs.length >= 3;

  if (practicePhase === 'prompt') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6"
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="w-full max-w-sm text-center space-y-6"
        >
          <Mascot mood="hearts_out" sport={preferredSport} size={80} className="mx-auto" />

          <div className="space-y-2">
            <h2 className="font-display font-extrabold text-2xl text-foreground">Out of hearts!</h2>
            <p className="text-sm text-muted-foreground font-body">
              Your next heart refills in <span className="font-display font-bold text-foreground">{timeStr}</span>. Come back and finish the lesson!
            </p>
          </div>

          <div className="bg-muted/50 rounded-xl px-4 py-3 border border-border/50">
            <p className="text-xs text-muted-foreground font-body flex items-center justify-center gap-1.5">
              <span className="text-base">💾</span>
              Your progress in this lesson has been saved
            </p>
          </div>

          <div className="space-y-3 pt-2">
            {canPractice && (
              <Button
                onClick={() => setPracticePhase('practicing')}
                className="w-full h-14 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
              >
                <span className="mr-2">⚡</span>
                Practice to earn a heart
              </Button>
            )}
            <Button
              onClick={onExit}
              variant="outline"
              className="w-full h-12 text-sm font-display font-bold rounded-xl border-2"
            >
              Exit lesson
            </Button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  if (practicePhase === 'done') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 15 }}
          className="text-center space-y-4"
        >
          <div className="text-6xl">❤️‍🔥</div>
          <h2 className="font-display font-extrabold text-2xl">Heart earned!</h2>
          <p className="text-sm text-muted-foreground font-body">Returning to your lesson...</p>
        </motion.div>
      </motion.div>
    );
  }

  // Practice phase
  if (!currentQ) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[100] bg-background flex flex-col"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/30">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <span className="text-sm font-display font-bold text-muted-foreground">
            Quick Practice
          </span>
          <span className="text-xs font-display font-bold text-primary">
            {qIndex + 1} / {practiceQs.length}
          </span>
        </div>
        <div className="max-w-lg mx-auto mt-2">
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-primary"
              animate={{ width: `${((qIndex + (selectedAnswer !== null ? 1 : 0)) / practiceQs.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-lg mx-auto space-y-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={qIndex}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              className="space-y-5"
            >
              <h3 className="font-display font-bold text-lg text-foreground leading-snug">
                {currentQ.question}
              </h3>

              <div className="space-y-2.5">
                {shuffled.shuffledOptions.map((opt, i) => {
                  const isSelected = selectedAnswer === i;
                  const isRight = i === shuffled.shuffledCorrectIndex;
                  const answered = selectedAnswer !== null;

                  return (
                    <button
                      key={i}
                      onClick={() => handleAnswer(i)}
                      disabled={answered}
                      className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all font-body text-sm ${
                        !answered
                          ? 'border-border hover:border-primary/40 bg-card'
                          : isSelected && isRight
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700'
                            : isSelected && !isRight
                              ? 'border-destructive bg-destructive/10 text-destructive'
                              : isRight
                                ? 'border-emerald-500/50 bg-emerald-500/5'
                                : 'border-border/50 bg-card/50 opacity-50'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>

              {selectedAnswer !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center pt-2"
                >
                  <Button
                    onClick={handleNext}
                    className="h-12 px-8 font-display font-bold rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    {qIndex < practiceQs.length - 1 ? 'Next' : 'Finish'}
                  </Button>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
